import React from "react";
import cn from "classnames";

import { useSelector } from "react-redux";

import { AudioContext } from "src/contexts";
import { createGetCount, createGetStatus } from "./selectors";
import { getDataTestId } from "src/utils";
import { getErrorMessage, getNextId, getTheme } from "src/selectors";
import { getLabel } from "./utils";
import { metrikaActions } from "src/store/actions";
import { BetProps } from "./types";
import { useAppDispatch, useContent } from "src/hooks";
import { betCreateSubmitThunk } from "src/thunks";

import styles from "./style.module.scss";

import Spinner from "src/assets/icons/spinner.svg";

export const useStart = ({ secondary }: BetProps) => {
  const content = useContent();

  const dispatch = useAppDispatch();

  const errorMessage = useSelector(getErrorMessage);
  const nextId = useSelector(getNextId);
  const theme = useSelector(getTheme);

  const count = useSelector(createGetCount(nextId));
  const status = useSelector(createGetStatus(nextId));

  const { play } = React.useContext(AudioContext);

  const onClick = React.useCallback(async () => {
    play("bet");

    const result = await dispatch(betCreateSubmitThunk());
    if (!result.hasError) {
      play("success");
    } else {
      play("error");
    }

    dispatch(metrikaActions.send({ target: "BetClick" }));
  }, [dispatch, play]);

  const getButtonProps = React.useCallback(
    () => ({
      className: cn(styles[content], styles[theme], {
        [styles.secondary]: secondary,
        [styles.basic]: status === "basic",
        [styles.disabled]: status === "disabled",
        [styles.error]: status === "error",
        [styles.loading]: status === "loading",
      }),
      disabled: status === "disabled" || status === "error" || status === "loading",
      onClick,
      ...getDataTestId("button-bet"),
    }),
    [content, onClick, secondary, status, theme]
  );

  const getIconProps = React.useCallback(
    () => ({
      className: cn(styles.icon),
      src: Spinner,
      ...getDataTestId("button-bet-loader"),
    }),
    []
  );

  const getCountProps = React.useCallback(
    (testId: string) => ({
      className: cn(styles.count),
      ...getDataTestId(testId),
    }),
    []
  );

  const declinations = React.useMemo(() => {
    if (
      content == "desktop" ||
      content == "mobile" ||
      content == "laptop" ||
      content == "v3"
    ) {
      return ["ПРОГНОЗОВ", "ПРОГНОЗА", "ПРОГНОЗ", "ПРОГНОЗОВ"];
    }
    return [];
  }, [content]);

  const single = React.useMemo(() => {
    switch (content) {
      case "desktop":
      case "v3":
        return "ПРОГНОЗОВ НЕТ";
      case "mobile":
      case "laptop":
        return "ВЫБЕРИТЕ ПРОГНОЗ";
      default:
        return [];
    }
  }, [content]);

  const label = React.useMemo(
    () => getLabel(count, declinations),
    [count, declinations]
  );

  const values = React.useMemo(
    () => ({
      content,
      count,
      label,
      errorMessage,
      single,
      status,
      styles,
    }),
    [content, count, errorMessage, label, single, status]
  );

  return {
    getButtonProps,
    getCountProps,
    getIconProps,
    values,
  };
};
